import { Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '@/assets/store/icon/logo.svg';
import usePhoneVerification from '@/domains/auth/hooks/usePhoneVerification';
import { usePendingAuthStore } from '@/domains/auth/store/pendingAuthStore';
import type { Gender, RegistrationChild, RegistrationForm } from '@/domains/auth/types/auth.types';
import { apiErrorBody, apiErrorMessage } from '@/domains/auth/utils/apiError';
import { VERIFICATION_ROUTE } from '@/domains/auth/utils/authRoutes';
import { normalizePhone } from '@/domains/auth/utils/phone';
import {
  birthDateError,
  emailError,
  passwordError,
  usernameError,
} from '@/domains/auth/utils/validation';
import AddressFields from '@/domains/mobile-web/components/AddressFields/AddressFields';
import DateTimeMobile from '@/domains/mobile-web/components/DateTimeMobile/DateTimeMobile';
import InputMobile from '@/domains/mobile-web/components/InputMobile/InputMobile';
import PasswordMobile from '@/domains/mobile-web/components/PasswordMobile/PasswordMobile';
import RadioGroupMobile, {
  type RadioOptionMobile,
} from '@/domains/mobile-web/components/RadioGroupMobile/RadioGroupMobile';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import type { AddressDraft, AddressDraftField } from '@/domains/mobile-web/types/address.types';
import {
  addressDraftErrors,
  addressDraftFieldOf,
  addressPlaceOf,
  EMPTY_ADDRESS_DRAFT,
} from '@/domains/mobile-web/utils/address';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import Button from '@/shared/components/ui/Button';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import Seo from '@/shared/components/ui/Seo/Seo';
import { DEFAULT_LANDING_ROUTE, LOGIN_ROUTE } from '@/shared/utils/loginRedirect';
import { showToast } from '@/shared/utils/showToast';

const GENDERS: RadioOptionMobile<Gender>[] = [
  { value: 'male', label: 'Laki-Laki' },
  { value: 'female', label: 'Perempuan' },
];

const MAX_CHILDREN = 10;

// key only keeps React rows stable while children are removed
type ChildDraft = RegistrationChild & { key: number };

type Errors = Record<string, string | undefined>;

type PersonalForm = Omit<RegistrationForm, 'address'>;

// Address errors are keyed address.<field>, as the server names them (regions without the _id suffix)
const ADDRESS_ERROR_PREFIX = 'address.';

const EMPTY_FORM: PersonalForm = {
  name: '',
  gender: 'male',
  date_of_birth: '',
  username: '',
  email: '',
  phone_number: '',
  children: [],
  password: '',
  confirm_password: '',
};

const validate = (form: PersonalForm, address: AddressDraft, children: ChildDraft[]): Errors => {
  const errors: Errors = {
    name: form.name.trim() ? undefined : 'Nama lengkap wajib diisi.',
    date_of_birth: birthDateError(form.date_of_birth),
    username: usernameError(form.username),
    email: emailError(form.email),
    phone_number: !form.phone_number.trim()
      ? 'Nomor HP wajib diisi.'
      : normalizePhone(form.phone_number)
        ? undefined
        : 'Nomor HP tidak valid. Contoh: 085776235639.',
    ...Object.fromEntries(
      Object.entries(addressDraftErrors(address)).map(([field, message]) => [
        `${ADDRESS_ERROR_PREFIX}${field}`,
        message,
      ])
    ),
    password: passwordError(form.password),
    confirm_password:
      form.confirm_password !== form.password ? 'Konfirmasi password tidak sesuai.' : undefined,
  };
  children.forEach((child, index) => {
    errors[`children.${index}.name`] = child.name.trim()
      ? undefined
      : 'Nama lengkap anak wajib diisi.';
    errors[`children.${index}.date_of_birth`] = birthDateError(child.date_of_birth);
  });
  return errors;
};

/** The error key of a server error `type`. */
const errorKeyOf = (type: string) =>
  type.startsWith(ADDRESS_ERROR_PREFIX)
    ? `${ADDRESS_ERROR_PREFIX}${addressDraftFieldOf(type.slice(ADDRESS_ERROR_PREFIX.length)) ?? ''}`
    : type;

const SignUp = () => {
  const navigate = useNavigate();
  // Back from Verifikasi: the form comes back as it was
  const pending = usePendingAuthStore((state) => state.registration);
  const pendingAddress = usePendingAuthStore((state) => state.registrationAddress);
  const [form, setForm] = useState<PersonalForm>(() => {
    if (!pending) return EMPTY_FORM;
    const { address: _address, ...personal } = pending;
    return personal;
  });
  const [address, setAddress] = useState<AddressDraft>(pendingAddress ?? EMPTY_ADDRESS_DRAFT);
  const nextKey = useRef(pending?.children.length ?? 0);
  const [children, setChildren] = useState<ChildDraft[]>(
    (pending?.children ?? []).map((child, key) => ({ ...child, key }))
  );
  const [errors, setErrors] = useState<Errors>({});
  const { turnstileRef, sent, send, resendIn, isSending } = usePhoneVerification();

  const setField = <K extends keyof PersonalForm>(field: K, value: PersonalForm[K]) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const changeAddress = (next: AddressDraft, field: AddressDraftField) => {
    setAddress(next);
    setErrors((current) => ({ ...current, [`${ADDRESS_ERROR_PREFIX}${field}`]: undefined }));
  };

  const addressErrors = Object.fromEntries(
    Object.entries(errors)
      .filter(([key]) => key.startsWith(ADDRESS_ERROR_PREFIX))
      .map(([key, message]) => [key.slice(ADDRESS_ERROR_PREFIX.length), message])
  );

  const setChild = <K extends keyof RegistrationChild>(
    index: number,
    field: K,
    value: RegistrationChild[K]
  ) => {
    setChildren((current) =>
      current.map((child, i) => (i === index ? { ...child, [field]: value } : child))
    );
    setErrors((current) => ({ ...current, [`children.${index}.${field}`]: undefined }));
  };

  const addChild = () => {
    setChildren((current) => [
      ...current,
      { key: nextKey.current++, name: '', gender: 'male', date_of_birth: '' },
    ]);
  };

  const removeChild = (index: number) => {
    setChildren((current) => current.filter((_, i) => i !== index));
    // Child errors are keyed by position, so they are recomputed on the next submit
    setErrors((current) =>
      Object.fromEntries(Object.entries(current).filter(([key]) => !key.startsWith('children.')))
    );
  };

  const isFilled =
    !!form.name.trim() &&
    !!form.date_of_birth &&
    !!form.username.trim() &&
    !!form.email.trim() &&
    !!form.phone_number.trim() &&
    !!addressPlaceOf(address) &&
    !!address.address_line.trim() &&
    !!form.password &&
    !!form.confirm_password &&
    children.every((child) => child.name.trim() && child.date_of_birth);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const nextErrors = validate(form, address, children);
    setErrors(nextErrors);
    const place = addressPlaceOf(address);
    if (!place || Object.values(nextErrors).some(Boolean)) return;

    const body: RegistrationForm = {
      ...form,
      name: form.name.trim(),
      username: form.username.trim(),
      email: form.email.trim(),
      address: place,
      children: children.map(({ key: _key, ...child }) => ({ ...child, name: child.name.trim() })),
    };
    // Back from Verifikasi with the same number: the code already sent is still the one to enter
    const codeStillValid =
      sent?.purpose === 'register' &&
      sent.phone === normalizePhone(body.phone_number) &&
      resendIn > 0;
    try {
      if (!codeStillValid) {
        await send({ purpose: 'register', ...body });
      }
      usePendingAuthStore.getState().setRegistration(body, address);
      navigate(VERIFICATION_ROUTE);
    } catch (err) {
      const { type } = apiErrorBody(err);
      const message = apiErrorMessage(err, 'Pendaftaran gagal.');
      const errorKey = type && errorKeyOf(type);
      if (errorKey && errorKey in nextErrors) {
        setErrors({ [errorKey]: message });
      } else {
        showToast({ type: 'error', title: 'Pendaftaran gagal', message });
      }
    }
  };

  const onBack = () => {
    usePendingAuthStore.getState().clear();
    const historyIndex = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (historyIndex > 0) {
      navigate(-1);
    } else {
      navigate(DEFAULT_LANDING_ROUTE, { replace: true });
    }
  };

  return (
    <main className="h-screen w-full overflow-hidden flex flex-col bg-[linear-gradient(180deg,#FFF_0%,#007AFF_250.56%)]">
      <Seo
        title="Buat Akun"
        description="Daftar akun Rumah Kebijaksanaan gratis untuk berbelanja buku, berdonasi, dan mendapatkan komisi afiliasi."
        path="/signup"
      />
      <HeaderMobile className="bg-transparent! w-full">
        <TopBarHeader type="back" title="Buat Akun" onBack={onBack} />
      </HeaderMobile>

      <WrapperMobile className="flex-1 overflow-auto mt-3">
        <form
          onSubmit={onSubmit}
          noValidate
          className="shadowCard p-6 flex-col flex gap-3 overflow-auto h-full flex-1"
        >
          {/* <figure>
            <img
              src={Logo}
              alt="logo rumah kebijaksanaan"
              loading="eager"
              className="w-auto h-auto"
              decoding="async"
            />
          </figure>

          <div className="w-full">
            <p className="title2medium text-black">Buat Akun</p>
            <p className="reg12 text-text-grey">Masukkan data anda untuk mendaftarkan.</p>
          </div> */}

          <section className="flex! flex-col gap-3">
            <p className="body1bold text-black">Data Pribadi</p>
            <InputMobile
              label="Nama Lengkap"
              name="name"
              placeholder="Masukkan nama lengkap anda"
              autoComplete="name"
              value={form.name}
              onChange={(event) => setField('name', event.target.value)}
              error={errors.name}
            />
            <RadioGroupMobile
              label="Jenis Kelamin"
              name="gender"
              options={GENDERS}
              value={form.gender}
              onChange={(value) => setField('gender', value)}
            />
            <DateTimeMobile
              label="Tanggal Lahir"
              name="date_of_birth"
              value={form.date_of_birth}
              onChange={(event) => setField('date_of_birth', event.target.value)}
              error={errors.date_of_birth}
            />
            <InputMobile
              label="Username"
              name="username"
              placeholder="Buat username anda"
              autoComplete="username"
              value={form.username}
              onChange={(event) => setField('username', event.target.value)}
              error={errors.username}
            />
            <InputMobile
              label="Alamat Email"
              name="email"
              type="email"
              placeholder="Masukkan alamat email anda"
              autoComplete="email"
              value={form.email}
              onChange={(event) => setField('email', event.target.value)}
              error={errors.email}
            />
            <InputMobile
              label="Nomor HP"
              name="phone_number"
              type="tel"
              inputMode="numeric"
              placeholder="Contoh: 085776235639"
              autoComplete="tel"
              helperText="Nomor HP akan digunakan untuk verifikasi akun."
              value={form.phone_number}
              onChange={(event) => setField('phone_number', event.target.value)}
              error={errors.phone_number}
            />
          </section>

          <div className="h-px w-full bg-blue-cs-200" />

          <section className="flex! flex-col gap-3">
            <p className="body1bold text-black">Alamat Utama</p>
            <AddressFields value={address} onChange={changeAddress} errors={addressErrors} />
          </section>

          <div className="h-px w-full bg-blue-cs-200" />

          <section className="flex! flex-col gap-3">
            <p className="body1bold text-black">Informasi Anak</p>
            {children.map((child, index) => (
              <div
                key={child.key}
                className="flex flex-col gap-4 rounded-[12px] border border-blue-cs-200 p-4"
              >
                <div className="flex items-center justify-between">
                  <p className="title2medium text-black">Anak {index + 1}</p>
                  <button
                    type="button"
                    onClick={() => removeChild(index)}
                    aria-label={`Hapus anak ${index + 1}`}
                    className="text-text-danger-500"
                  >
                    <Trash2 className="size-5" />
                  </button>
                </div>
                <InputMobile
                  label="Nama Lengkap"
                  name={`children.${index}.name`}
                  placeholder="Masukkan nama lengkap anak"
                  value={child.name}
                  onChange={(event) => setChild(index, 'name', event.target.value)}
                  error={errors[`children.${index}.name`]}
                />
                <RadioGroupMobile
                  label="Jenis Kelamin"
                  name={`children.${child.key}.gender`}
                  options={GENDERS}
                  value={child.gender}
                  onChange={(value) => setChild(index, 'gender', value)}
                />
                <DateTimeMobile
                  label="Tanggal Lahir"
                  name={`children.${index}.date_of_birth`}
                  value={child.date_of_birth}
                  onChange={(event) => setChild(index, 'date_of_birth', event.target.value)}
                  error={errors[`children.${index}.date_of_birth`]}
                />
              </div>
            ))}

            {children.length < MAX_CHILDREN && (
              <Button type="button" className="btn-fill-white w-full" onClick={addChild}>
                <Plus className="size-4 text-text-blue-100" />
                <span className="body1medium text-text-blue-100">Tambah informasi anak</span>
              </Button>
            )}
          </section>

          <div className="h-px w-full bg-blue-cs-200" />

          <section className="flex! flex-col gap-3">
            <p className="body1bold text-black">Keamanan Akun</p>
            <PasswordMobile
              label="Password"
              name="password"
              placeholder="Buat password"
              autoComplete="new-password"
              value={form.password}
              onChange={(event) => setField('password', event.target.value)}
              error={errors.password}
            />
            <PasswordMobile
              label="Konfirmasi Password"
              name="confirm_password"
              placeholder="Masukkan kembali password"
              autoComplete="new-password"
              value={form.confirm_password}
              onChange={(event) => setField('confirm_password', event.target.value)}
              error={errors.confirm_password}
            />
          </section>

          <div className="flex flex-col gap-4 mt-3">
            <Button type="submit" customButton="btn-fill-blue" disabled={!isFilled || isSending}>
              <span className="title2medium text-white">
                {isSending ? 'Memproses...' : 'Daftar'}
              </span>
            </Button>
            <p className="reg12 text-text-grey text-center">
              Sudah punya akun?{' '}
              <Link to={LOGIN_ROUTE} className="text-text-blue-100">
                Masuk
              </Link>
            </p>
            {/* Turnstile shows a checkbox here only when it needs one */}
            <div ref={turnstileRef} className="flex justify-center empty:hidden" />
          </div>
        </form>
      </WrapperMobile>
    </main>
  );
};

export default SignUp;
